import type {Dayjs} from 'dayjs'
import {IconPlus} from '@tabler/icons-react'
import {formatTime} from '../formatter/formatTime.ts'
import {Button, type ButtonProps, Card} from '@mantine/core'
import {useCallback, useState} from 'react'
import type {ProjectId} from '@package/shared'
import type {AddTimeEntryRequest} from '@app/api-resolver/router'
import type {DateRange} from '../../../components/dateRangeSelector/DateRangeSelector.tsx'
import {AddTimeEntryForm} from '../forms/timeEntry/AddTimeEntryForm.tsx'
import {useDayItems} from '../../../providers/dayItems/useDayItems.ts'
import {useUserSettings} from '../../../providers/userSettings/useUserSettings.ts'

type Props = {
  disabled: boolean
  projectId: ProjectId | null
  day: DateRange
  start: Dayjs | undefined
  end: Dayjs | undefined
  customLabel?: string
} & Omit<ButtonProps, 'onClick' | 'disabled' | 'loading' | 'leftSection'>

export const InsertTimeEntryButton = ({disabled, projectId, day, start, end, customLabel, ...rest}: Props) => {
  const {userSettings} = useUserSettings()
  const {addTimeEntry} = useDayItems()
  const [adding, setAdding] = useState<boolean>(false)
  const [timeEntry, setTimeEntry] = useState<Partial<AddTimeEntryRequest>>()

  const handleClick = useCallback(() => {
    setTimeEntry({start: start?.toISOString(), end: end?.toISOString(), projectId: projectId || undefined})
  }, [end, projectId, start])

  if (start?.isSame(end)) return null
  if (start?.isAfter(end)) return null

  if (start?.isSame(day.end)) return null
  if (end?.isSame(day.start)) return null

  if (timeEntry) {
    return (
      <Card p={'xs'}>
        <AddTimeEntryForm
          day={day}
          entry={{
            start: start?.toISOString(),
            end: end?.toISOString(),
            projectId: projectId || undefined,
          }}
          onAdd={async (request) => {
            setAdding(true)
            await addTimeEntry(request).finally(() => setAdding(false))
            setTimeEntry(undefined)
          }}
          adding={adding}
          onCancel={async () => setTimeEntry(undefined)}
          disabled={disabled}
        />
      </Card>
    )
  }

  return (
    <Button
      {...rest}
      size={'compact-sm'}
      variant={'subtle'}
      color={rest.color}
      onClick={handleClick}
      leftSection={<IconPlus stroke={1} size={16} />}
      disabled={disabled}
    >
      {customLabel || //
        (start && end && `${formatTime(start, userSettings)} - ${formatTime(end, userSettings)}`) ||
        'Insert'}
    </Button>
  )
}
